// =============================================================================
// WhatsApp day-closing message + direct WhatsApp links (no WhatsApp API needed)
// =============================================================================
const rs = (n) => {
    const v = Number(n) || 0;
    const paise = Math.abs(v % 1) > 0.004;
    return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: paise ? 2 : 0, maximumFractionDigits: 2 })}`;
};
const longDay = (ymd) => {
    const [y, m, d] = String(ymd || '').split('-').map(Number);
    if (!y) return '';
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** 98765 43210 / +91-9876543210 / 919876543210 -> 919876543210 (Indian numbers get 91) */
export const toWaNumber = (phone) => {
    const d = String(phone || '').replace(/\D/g, '');
    if (d.length === 10) return `91${d}`;
    if (d.length === 11 && d.startsWith('0')) return `91${d.slice(1)}`;
    return d;
};
export const isValidWaNumber = (phone) => toWaNumber(phone).length >= 11;

/** Opens WhatsApp with the message (to a number, or lets the user choose a chat when no number) */
export const waUrl = (phone, text) => {
    const n = phone ? toWaNumber(phone) : '';
    return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
};

/** Pretty number for lists: 919876543210 -> +91 98765 43210 */
export const prettyPhone = (phone) => {
    const n = toWaNumber(phone);
    return n.length === 12 && n.startsWith('91') ? `+91 ${n.slice(2, 7)} ${n.slice(7)}` : `+${n}`;
};

/**
 * The day-closing message for the owner.
 * report = /daily-report data, link = view-only closing page (optional)
 */
export const buildClosingMessage = ({ report, storeName, link }) => {
    const t = report.totals; const p = report.payments; const d = report.drawer; const r = report.dayResult; const ex = report.expenses || {};
    const L = [];
    const line = (label, value) => L.push(`• ${label}: *${value}*`);

    L.push(`*🧾 Day closing, ${storeName}*`);
    L.push(`📅 ${longDay(report.date)}`);
    L.push('');

    L.push(`*✅ Left today: ${rs(r.net)}*`);
    L.push(`_Money in ${rs(r.moneyIn)} − expenses ${rs(r.expensesTotal)}_`);
    L.push('');

    L.push(`*💰 Money in: ${rs(r.moneyIn)}*`);
    line('Cash', rs(p.cash));
    line('UPI', rs(p.upi));
    line('Card', rs(p.card));
    if (report.outside?.total > 0) line('Old udhaar / advance received', rs(report.outside.total));
    L.push('');

    L.push(`*💸 Expenses: ${rs(r.expensesTotal)}*`);
    line('Paid in cash', rs(r.expensesCash));
    line('Paid online', rs(r.expensesOnline));
    if ((ex.byCategory || []).length) L.push(`  ${ex.byCategory.slice(0, 3).map((c) => `${c.name} ${rs(c.amount)}`).join(', ')}`);
    if (ex.vendor?.paid > 0) L.push(`  🚚 Paid to vendors ${rs(ex.vendor.paid)}${ex.vendor.notCleared > 0 ? ` (${rs(ex.vendor.notCleared)} not cleared)` : ''}`);
    if (ex.pendingCount > 0) L.push(`  ⏳ ${ex.pendingCount} waiting for approval (${rs(ex.pending)})`);
    L.push('');

    // stock bought from vendors today (paid or on credit)
    const vp = report.vendorPurchases;
    if (vp && vp.count > 0) {
        L.push(`*🚚 Vendor purchases: ${rs(vp.total)}*`);
        line('Paid', rs(vp.paid));
        if (vp.toPay > 0) line('To pay later', rs(vp.toPay));
        vp.items.slice(0, 4).forEach((b) => L.push(`  ${b.vendorName}${b.billNo ? ` (${b.billNo})` : ''}: ${rs(b.total)}${b.toPay > 0 ? `, ${rs(b.toPay)} due` : ', paid'}`));
        if (vp.items.length > 4) L.push(`  +${vp.items.length - 4} more bills`);
        L.push('');
    }

    L.push('*🏦 Cash drawer*');
    line('Should be in the drawer', rs(d.expected));
    if (d.expected < -0.009) L.push(`  ⚠️ Below zero: ${rs(Math.abs(d.expected))} paid from someone's pocket`);
    if (d.closing) {
        const diff = d.closing.difference;
        const tag = Math.abs(diff) < 0.01 ? 'matches ✔' : `${diff < 0 ? 'short' : 'extra'} ${rs(Math.abs(diff))}`;
        line('Counted', `${rs(d.closing.countedCash)} (${tag})`);
    } else {
        L.push('• Counted: _not counted yet_');
    }
    if (d.bankDeposit > 0) line('Deposited in bank', rs(d.bankDeposit));
    if (d.ownerWithdrawal > 0) line('Owner took', rs(d.ownerWithdrawal));
    L.push('');

    L.push('*📊 Business*');
    line('Total sales', rs(t.grossSales));
    line('Bills', `${t.bills}  ·  Clients ${t.customersServed}${t.newClients ? ` (${t.newClients} new)` : ''}`);
    if (p.advanceUsed > 0) line('Khata / prepaid used', rs(p.advanceUsed));
    if (p.dueCreated > 0) line('Udhaar given', rs(p.dueCreated));
    if (report.sales?.loyaltySold > 0) line('Memberships / packages sold', rs(report.sales.loyaltySold));
    if (report.staff?.[0]) line('Top staff', `${report.staff[0].name} ${rs(report.staff[0].credit)}`);

    if (link) {
        L.push('');
        L.push('🔗 *Full closing details:*');
        L.push(link);
    }
    L.push('');
    L.push('_Sent from Hirsta_');
    return L.join('\n');
};
